// Pure rendering primitives for compose layers (text + shapes).
//
// Used by:
//   - Step 6 preview (DOM render via getTextLayerStyle)
//   - Preset thumbnails (canvas render via renderLayersToCanvas)
//   - Step 5 download flatten (canvas render via renderLayersToCanvas)
//
// Editor-only fields (id, imageIndex, positionId) live on the wrapper types
// in the editor (e.g. TextOverlay in StepText). This module only deals
// with what's needed to put pixels on a canvas or styles on a DOM node.

import type { CSSProperties } from 'react';

// ---------- Types ----------

export type OverlayAnchor =
  | 'tl' | 'tc' | 'tr'
  | 'ml' | 'mc' | 'mr'
  | 'bl' | 'bc' | 'br';

export type OverlayTextAlign = 'left' | 'center' | 'right';

export type OverlayTextTransform = 'none' | 'uppercase' | 'lowercase';

// What every rendered text layer needs. Editor types extend this.
export interface TextLayer {
  kind: 'text';
  text: string;
  x: number;            // 0..1, position of the anchor point on the canvas
  y: number;            // 0..1
  anchor: OverlayAnchor;
  maxWidthFrac: number; // wrap width as a fraction of canvas width
  textAlign: OverlayTextAlign;
  fontSize: number;     // px at a 1200-wide reference canvas; scaled at render time
  fontWeight: string;
  fontFamily: string;
  color: string;
  textTransform: OverlayTextTransform;
  // Optional background that hugs the rendered text + padding on all four sides.
  // When set, the anchor positions the BACKGROUND box (not the wrap-width box),
  // so the background grows and shrinks with the text content.
  backgroundColor?: string;
  backgroundPadding?: number;  // px at the 1200 reference; symmetric on all sides
  // Corner radius for the backgroundColor box (+ offsetShadow), px at the 1200
  // reference. Enables pill-shaped CTAs (set large — the renderer clamps to
  // half the box's smaller side, so a big value = full pill). 0/undefined =
  // square corners (legacy behavior). DOM uses border-radius; canvas uses
  // roundRect. Brought over from the JSON editor's button(pill) model.
  backgroundRadius?: number;
  // When set, the renderer first lets text wrap up to `maxWidthFrac`, then if
  // wrapping exceeds `maxLines` it iteratively shrinks fontSize down to 40%
  // until the content fits. Use for presets that should remain readable as
  // copy length varies — the box still grows with the text up to the wrap cap.
  maxLines?: number;
  // Live-stacking metadata. When `stackOnPrevious` is true, the layer's `y`
  // is recomputed at RENDER time (via resolveStackedLayers) as the previous
  // text layer's measured bottom + `stackGap`. This makes the tagline reflow
  // whenever the headline changes length — both for AI copy variation and
  // for in-editor text editing. Assumes a top-aligned anchor ('tl' / 'tc' / 'tr').
  // Dragging a stacked layer should clear `stackOnPrevious` (the caller's job)
  // so the manual position takes over.
  stackOnPrevious?: boolean;
  stackGap?: number;    // px at the 1200 reference; vertical gap between blocks
  // Outline stroke applied to the rendered glyphs. When `textStrokeColor` is
  // set the renderer (both DOM and canvas) draws an outline of that color and
  // `textStrokeWidth` px width at the 1200 reference (scaled at draw time).
  // Combine with `color: 'transparent'` for hollow outline-only text, or with
  // a solid `color` for a stroked filled glyph. Layers with `textStrokeColor`
  // are skipped by auto-contrast — the author owns the fill/stroke combo.
  textStrokeColor?: string;
  textStrokeWidth?: number;
  // Optional offset shadow rectangle drawn UNDER the backgroundColor rect.
  // Same size as the bg (text + padding), shifted by (offsetX, offsetY) px at
  // the 1200 reference. Auto-grows with text length just like the main bg —
  // that's the whole point: it's an extension of the bg, not a separate shape.
  // Used for the "offset shadow" / "drop block" visual pattern.
  offsetShadow?: {
    color: string;
    offsetX: number;
    offsetY: number;
  };
}

// Decorative shape layer. Stubbed for Step 6 preset work; rect/line/circle
// cover the cases in the reference screenshots (vertical bars, accent blocks).
export type ShapeKind = 'rect' | 'line' | 'circle';

export interface ShapeLayer {
  kind: 'shape';
  shape: ShapeKind;
  x: number; y: number;     // 0..1 — top-left of the shape's bounding box
  w: number; h: number;     // 0..1 of canvas dims
  color: string;
  opacity: number;          // 0..1
  rotation?: number;        // degrees, optional
}

export type Layer = TextLayer | ShapeLayer;

// ---------- Preset types ----------

// All platform aspect ratios we support end-to-end. Backend's SIZE_MAP must
// have an entry for each. Text presets are keyed by AspectRatio in their
// `layouts` field — most templates today only define a '1:1' layout, so
// other aspects gracefully fall back to "no presets available" until they
// get authored in the preset editor.
export type AspectRatio = '1:1' | '4:5' | '2:3' | '9:16' | '1.91:1' | '16:9';

/** Tailwind class for an aspect ratio. MUST list every supported ratio as a
 * static literal — Tailwind's JIT extractor doesn't see runtime-constructed
 * strings like `aspect-[${w}/${h}]`, so dynamic class names render at zero
 * height. Add new ratios here AND to the AspectRatio type union. */
export function aspectClass(ratio: string): string {
  switch (ratio) {
    case '1:1':    return 'aspect-square';
    case '4:5':    return 'aspect-[4/5]';
    case '2:3':    return 'aspect-[2/3]';
    case '9:16':   return 'aspect-[9/16]';
    case '16:9':   return 'aspect-[16/9]';
    case '1.91:1': return 'aspect-[1.91/1]';
    default:       return 'aspect-square';
  }
}

/** Numerical W/H ratio for an aspect string (e.g. '9:16' → 0.5625). */
export function aspectToRatio(aspect: string): number {
  const [w, h] = aspect.split(':').map(Number);
  if (!w || !h) return 1;
  return w / h;
}

/** Reference height in REFERENCE_CANVAS_WIDTH-wide space for the given aspect.
 * Used by resolveStackedLayers callers + canvas drawers to keep math
 * aspect-agnostic. (Hardcodes 1200 instead of REFERENCE_CANVAS_WIDTH because
 * the constant is declared later in this module.) */
export function aspectToRefHeight(aspect: string): number {
  return 1200 / aspectToRatio(aspect);
}

// What the AI fills in for each text slot. Editor seeds from suggestCopy:
//   - 'headline' / 'tagline' come from suggestedFrames[frameIndex]
//   - 'subtext' is a generic placeholder for now; later wired to per-layer AI copy
//   - 'cta' / 'static' use the preset's staticText
export type PresetRole = 'headline' | 'tagline' | 'subtext' | 'cta' | 'static';

// Spec for a text slot in a preset. Same rendering shape as TextLayer but the
// actual `text` is resolved from suggestedFrames at apply-time using `role`.
// stackOnPrevious / stackGap are inherited from TextLayer and resolved at
// render time (see resolveStackedLayers).
export interface PresetTextLayer extends Omit<TextLayer, 'text'> {
  role: PresetRole;
  staticText?: string;  // used when role is 'static' or 'cta' with a fixed string
}

export type PresetLayer = PresetTextLayer | ShapeLayer;

export interface PresetLayout {
  layers: PresetLayer[];  // ordered; first = bottom (drawn first), last = top
}

export interface TextPreset {
  id: string;
  name: string;
  // Partial: some compositions only work in one aspect ratio. The gallery
  // hides presets whose current-AR layout is undefined.
  layouts: Partial<Record<AspectRatio, PresetLayout>>;
}

// ---------- Editor-wrapper types ----------
// Adds runtime metadata (id, imageIndex) onto rendering layers. Live in this
// module so both StepText (editor) and downstream consumers share them.

export interface TextOverlay extends TextLayer {
  id: string;
  imageIndex: number;
  positionId: string;   // '' = freely dragged (no active snap shortcut)
}

export interface ShapeOverlay extends ShapeLayer {
  id: string;
  imageIndex: number;
}

// Brand-logo overlay — wired in on the final wizard step (Add Text & Logo).
// Lives in its own array (not a Layer subtype) because the logo source is a
// raster image URL with a natural aspect ratio, not authored geometry:
//   - width is user-controlled as a fraction of canvas width
//   - height is derived from the image's natural aspect, never authored
//   - canvas render path is async (image preload) — handled inline in the
//     flatten path rather than the synchronous renderLayersToCanvas pipeline
// Position is anchored top-left for simplicity (text uses 9-anchor system,
// overkill for a single decorative element).
export type LogoTint = 'original' | 'black' | 'white';

export interface LogoOverlay {
  id: string;
  imageIndex: number;
  src: string;           // logo image URL (public Supabase URL, CORS-readable)
  x: number;             // 0..1 — top-left x fraction of canvas
  y: number;             // 0..1 — top-left y fraction of canvas
  widthFrac: number;     // 0..1 — width as fraction of canvas; height derives from natural aspect
  opacity: number;       // 0..1
  // Monochrome silhouette toggle. When 'black' or 'white' the alpha
  // channel is preserved and every opaque pixel is recolored:
  //   - DOM uses CSS `filter: brightness(0)` (black) or
  //     `filter: brightness(0) invert(1)` (white).
  //   - Canvas export uses an offscreen with globalCompositeOperation
  //     = 'source-in' filled with the target color. Both produce a
  //     clean monochrome stamp that reads against any background.
  // 'original' = no filter (full-color logo).
  tint?: LogoTint;
  // Optional 3×3 placement-grid id (top-left / top-center / … /
  // bottom-right) — set when the user clicks a placement cell in the
  // editor, cleared back to '' on free drag. Pure UI state; the
  // renderer reads x/y/widthFrac only.
  positionId?: string;
}

// AI-suggested copy for a single frame. Returned by /api/suggest-copy and
// consumed when applying a preset to populate role-based text slots.
export interface SuggestedFrame {
  headline: string;
  tagline: string;
  subtext?: string;   // optional — wired in when backend suggest-copy extends to subtext
}

// ---------- Preset application ----------

// Default placeholder strings when AI hasn't produced copy for a role yet.
const DEFAULT_TEXT_BY_ROLE: Record<PresetRole, string> = {
  headline: 'Your Headline',
  tagline: 'Your tagline',
  subtext: 'Add subtext',
  cta: 'Shop Now',
  static: '',
};

function resolveLayerText(layer: PresetTextLayer, frame: SuggestedFrame | undefined): string {
  if (layer.role === 'static' || layer.role === 'cta') {
    return layer.staticText ?? DEFAULT_TEXT_BY_ROLE[layer.role];
  }
  if (layer.role === 'headline') return frame?.headline || DEFAULT_TEXT_BY_ROLE.headline;
  if (layer.role === 'tagline') return frame?.tagline || DEFAULT_TEXT_BY_ROLE.tagline;
  if (layer.role === 'subtext') return frame?.subtext || DEFAULT_TEXT_BY_ROLE.subtext;
  return '';
}

// Measure how many lines a text wraps to under a given font config. Uses a
// throwaway canvas — DOM-only, returns 1 if no canvas is available.
function measureLineCount(
  text: string,
  fontSize: number,
  fontFamily: string,
  fontWeight: string,
  maxWidthPx: number,
): number {
  if (typeof document === 'undefined') return 1;
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');
  if (!ctx) return 1;
  const weight = fontWeightToNumber(fontWeight);
  ctx.font = `${weight} ${fontSize}px ${fontFamily}, Inter, system-ui, sans-serif`;
  return wrapTextToWidth(ctx, text, maxWidthPx).length;
}

// If the layer declares maxLines, iteratively shrink fontSize (in 8% steps,
// down to 40% of the original) until the wrapped text fits within that many
// lines. Returns the original fontSize when maxLines is unset or already
// satisfied. Operates in REFERENCE_CANVAS_WIDTH space so the result is
// independent of the render target — caller scales at draw time.
export function getEffectiveFontSize(layer: TextLayer): number {
  const base = layer.fontSize;
  if (!layer.maxLines || layer.maxLines < 1) return base;
  if (typeof document === 'undefined') return base;
  const c = document.createElement('canvas');
  const ctx = c.getContext('2d');
  if (!ctx) return base;
  const text = applyTextTransform(layer.text, layer.textTransform);
  const maxWidth = REFERENCE_CANVAS_WIDTH * layer.maxWidthFrac;
  const weight = fontWeightToNumber(layer.fontWeight);
  const minSize = base * 0.4;
  let size = base;
  for (let i = 0; i < 20; i++) {
    ctx.font = `${weight} ${size}px ${layer.fontFamily}, Inter, system-ui, sans-serif`;
    const lines = wrapTextToWidth(ctx, text, maxWidth);
    if (lines.length <= layer.maxLines) return size;
    const next = Math.max(minSize, size * 0.92);
    if (next >= size) return next;  // hit the floor
    size = next;
  }
  return size;
}

// Compute the bottom edge (fractional, of refH) of a text layer, given its
// current `y` and the layer's resolved text. Used by resolveStackedLayers to
// stack the next layer beneath the previous one's measured extent.
function computeTextLayerBottomFrac(layer: TextLayer, refH: number): number {
  const transformedText = applyTextTransform(layer.text, layer.textTransform);
  const effFontSize = getEffectiveFontSize(layer);
  const lineCount = measureLineCount(
    transformedText,
    effFontSize,
    layer.fontFamily,
    layer.fontWeight,
    layer.maxWidthFrac * REFERENCE_CANVAS_WIDTH,
  );
  const padPx = layer.backgroundPadding ?? 0;
  const boxHPx = lineCount * effFontSize * 1.2 + 2 * padPx;
  const boxHFrac = boxHPx / refH;
  const { ay } = anchorOffsets(layer.anchor);
  const topFrac = layer.y - ay * boxHFrac;
  return topFrac + boxHFrac;
}

// Walk layers in order and recompute `y` for any text layer whose
// `stackOnPrevious` flag is set, placing it at `(previous text layer's
// measured bottom) + stackGap`. Returns a new array with resolved positions —
// the input is untouched. Shapes pass through unchanged and do not participate
// in stacking (only text layers act as a stack base; this matches current
// preset patterns and keeps the rule simple).
//
// Run this once per render, on the (per-frame) layer array, BEFORE laying out
// the DOM or drawing the canvas. The canvas renderer (`renderLayersToCanvas`)
// already does it internally, so canvas callers don't need to.
export function resolveStackedLayers(
  layers: Layer[],
  canvasW: number,
  canvasH: number,
): Layer[] {
  // Normalize to reference space so the result is independent of the actual
  // render-target size. fontSize is authored at REFERENCE_CANVAS_WIDTH; we
  // measure there and turn pixel heights into fractions.
  const refW = REFERENCE_CANVAS_WIDTH;
  const refH = canvasW > 0 ? (canvasH / canvasW) * refW : REFERENCE_CANVAS_WIDTH;

  let prevTextBottomFrac: number | null = null;
  return layers.map((layer) => {
    if (layer.kind === 'shape') return layer;

    let y = layer.y;
    if (layer.stackOnPrevious && prevTextBottomFrac !== null) {
      const gapFrac = (layer.stackGap ?? 0) / refH;
      y = prevTextBottomFrac + gapFrac;
    }
    const resolved: TextLayer = { ...layer, y };
    prevTextBottomFrac = computeTextLayerBottomFrac(resolved, refH);
    return resolved;
  });
}

// Apply a preset across all frames. Returns the full replacement set for
// textOverlays + shapeOverlays — caller wholesale-replaces editor state
// for the affected frames.
//
// Stack metadata (`stackOnPrevious`, `stackGap`) is preserved on the overlay
// and resolved at render time by `resolveStackedLayers` — so editing copy
// after apply (or AI copy varying in length) automatically reflows stacked
// layers. The `y` here is the preset's authored fallback; the renderer
// overrides it when stacking is active.
export function applyPreset(
  preset: TextPreset,
  aspectRatio: AspectRatio,
  frameCount: number,
  suggestedFrames: SuggestedFrame[],
): { texts: TextOverlay[]; shapes: ShapeOverlay[] } {
  // Prefer an aspect-specific authored layout. When the preset hasn't been
  // authored for the requested aspect, fall back to the '1:1' layout — the
  // renderer's fractional positions adapt to any canvas, so a 1:1 layout
  // applied to 9:16 / 16:9 etc. still produces sensible (if not perfectly
  // tuned) output. Users can author aspect-specific tweaks in the editor.
  const layout = preset.layouts[aspectRatio] ?? preset.layouts['1:1'];
  if (!layout) return { texts: [], shapes: [] };

  const texts: TextOverlay[] = [];
  const shapes: ShapeOverlay[] = [];
  const stamp = Date.now();

  for (let frameIdx = 0; frameIdx < frameCount; frameIdx++) {
    const frame = suggestedFrames[frameIdx];
    layout.layers.forEach((layer, layerIdx) => {
      if (layer.kind === 'text') {
        const resolvedText = resolveLayerText(layer, frame);
        texts.push({
          ...layer,
          kind: 'text',
          id: `p${stamp}-f${frameIdx}-t${layerIdx}`,
          imageIndex: frameIdx,
          positionId: '',
          text: resolvedText,
        });
      } else {
        shapes.push({
          ...layer,
          id: `p${stamp}-f${frameIdx}-s${layerIdx}`,
          imageIndex: frameIdx,
        });
      }
    });
  }

  return { texts, shapes };
}

// ---------- Auto contrast ----------

// Sample the average perceived luminance of the rectangle (xFrac..xFrac+wFrac,
// yFrac..yFrac+hFrac) of `img`. Returns 0..1 where 0 = black, 1 = white.
function regionLuminance(
  ctx: CanvasRenderingContext2D,
  imgW: number,
  imgH: number,
  xFrac: number,
  yFrac: number,
  wFrac: number,
  hFrac: number,
): number {
  const sx = Math.max(0, Math.floor(xFrac * imgW));
  const sy = Math.max(0, Math.floor(yFrac * imgH));
  const sw = Math.max(1, Math.min(imgW - sx, Math.ceil(wFrac * imgW)));
  const sh = Math.max(1, Math.min(imgH - sy, Math.ceil(hFrac * imgH)));
  const data = ctx.getImageData(sx, sy, sw, sh).data;
  let total = 0;
  const pixels = data.length / 4;
  // Step through every 4th pixel for speed; the sample is dense enough that
  // the average is unchanged in practice.
  const stride = 4 * 4;
  let counted = 0;
  for (let p = 0; p < data.length; p += stride) {
    const r = data[p];
    const g = data[p + 1];
    const b = data[p + 2];
    // ITU-R BT.601 perceived-luminance weighting.
    total += (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    counted++;
  }
  return counted > 0 ? total / counted : pixels > 0 ? 0.5 : 0.5;
}

// Estimate the text layer's bounding box (fractional, of the canvas) using its
// anchor + maxWidthFrac + fontSize. Approximate; we only need a region to
// sample, not pixel-accurate bounds.
function approximateTextRegion(layer: TextLayer): {
  x: number; y: number; w: number; h: number;
} {
  const { ax, ay } = anchorOffsets(layer.anchor);
  const wFrac = Math.min(0.5, Math.max(0.1, layer.maxWidthFrac));
  const hFrac = Math.min(0.4, Math.max(0.04, (layer.fontSize * 1.4) / REFERENCE_CANVAS_WIDTH));
  const x = Math.max(0, Math.min(1 - wFrac, layer.x - ax * wFrac));
  const y = Math.max(0, Math.min(1 - hFrac, layer.y - ay * hFrac));
  return { x, y, w: wFrac, h: hFrac };
}

// Pick black or white for a plain text layer given a ctx that has the
// background already painted. Synchronous companion to
// autoAdjustOverlayColors — used by thumbnail rendering, where the canvas
// context is already in hand.
export function pickAutoContrastColor(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
  layer: TextLayer,
): '#000000' | '#FFFFFF' {
  const region = approximateTextRegion(layer);
  const lum = regionLuminance(ctx, canvasW, canvasH, region.x, region.y, region.w, region.h);
  return lum > 0.55 ? '#000000' : '#FFFFFF';
}

// Flip the `color` of every plain-text overlay (i.e. no backgroundColor) to
// black or white based on the average luminance of the region behind it on
// its frame's generated image. Layers with a backgroundColor are returned
// unchanged — they already have built-in contrast.
//
// Loads each image once via the DOM Image API; safe to call any time after
// generation. Falls back to leaving overlays untouched on load errors.
export async function autoAdjustOverlayColors(
  overlays: TextOverlay[],
  generatedImages: string[],
): Promise<TextOverlay[]> {
  if (typeof document === 'undefined' || overlays.length === 0) return overlays;

  // Group overlays by frame so we only decode each image once.
  const byFrame = new Map<number, TextOverlay[]>();
  for (const o of overlays) {
    if (!byFrame.has(o.imageIndex)) byFrame.set(o.imageIndex, []);
    byFrame.get(o.imageIndex)!.push(o);
  }

  const result: TextOverlay[] = [...overlays];
  const indexById = new Map(result.map((o, i) => [o.id, i] as const));

  await Promise.all(
    Array.from(byFrame.entries()).map(async ([frameIdx, frameLayers]) => {
      const src = generatedImages[frameIdx];
      if (!src) return;
      const img = await new Promise<HTMLImageElement | null>((resolve) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => resolve(null);
        i.src = src;
      });
      if (!img) return;
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || 1024;
      canvas.height = img.naturalHeight || 1024;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0);

      for (const layer of frameLayers) {
        if (layer.backgroundColor) continue;
        // Layers with explicit stroke styling own their color choice.
        if (layer.textStrokeColor) continue;
        const region = approximateTextRegion(layer);
        const lum = regionLuminance(ctx, canvas.width, canvas.height, region.x, region.y, region.w, region.h);
        // Bias slightly toward white text on mid-tone images — pure black on
        // a 50% gray looks heavier than pure white on the same gray.
        const color = lum > 0.55 ? '#000000' : '#FFFFFF';
        const idx = indexById.get(layer.id);
        if (idx !== undefined) result[idx] = { ...result[idx], color };
      }
    }),
  );

  return result;
}

// ---------- Background → suggested text colors ----------

function _hexClamp(n: number): string {
  return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
}
function rgbToHexStr(r: number, g: number, b: number): string {
  return `#${_hexClamp(r)}${_hexClamp(g)}${_hexClamp(b)}`.toUpperCase();
}
function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return [h, s, l];
}
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

// Sample a generated frame, pull its dominant CHROMATIC colors (skipping
// greys + near-white/black — black/white are offered as their own group),
// and turn each into a legible text color: rotate ~180° to the complementary
// hue, keep it vivid, and set lightness to contrast the overall background
// luminance. Returns up to `max` deduped #RRGGBB hexes, or [] when the image
// is all-neutral / undecodable. Pure browser util (no external palette dep).
export async function suggestTextColorsFromImage(
  src: string,
  max = 3,
): Promise<string[]> {
  if (typeof document === 'undefined' || !src) return [];
  const img = await new Promise<HTMLImageElement | null>((resolve) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => resolve(null);
    i.src = src;
  });
  if (!img) return [];
  const S = 48;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];
  ctx.drawImage(img, 0, 0, S, S);
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, S, S).data;
  } catch {
    return []; // tainted canvas — defensive (data-URL frames are same-origin)
  }
  const buckets = new Map<string, { r: number; g: number; b: number; n: number }>();
  let lumSum = 0, lumN = 0;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
    if (a < 200) continue;
    lumSum += (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    lumN++;
    const key = `${r >> 5}-${g >> 5}-${b >> 5}`;
    const e = buckets.get(key) ?? { r: 0, g: 0, b: 0, n: 0 };
    e.r += r; e.g += g; e.b += b; e.n++;
    buckets.set(key, e);
  }
  const bgLum = lumN ? lumSum / lumN : 0.5;
  const dominant = Array.from(buckets.values())
    .map((e) => ({ r: e.r / e.n, g: e.g / e.n, b: e.b / e.n, n: e.n }))
    .filter((e) => {
      const [, s, l] = rgbToHsl(e.r, e.g, e.b);
      return s > 0.12 && l > 0.08 && l < 0.95; // skip greys + near-white/black
    })
    .sort((a, b) => b.n - a.n);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const c of dominant) {
    let [h, s] = rgbToHsl(c.r, c.g, c.b);
    h = (h + 180) % 360;                  // complementary hue
    s = Math.min(1, Math.max(0.55, s));   // keep it vivid
    const l = bgLum > 0.5 ? 0.32 : 0.7;   // contrast the background for legibility
    const [r, g, b] = hslToRgb(h, s, l);
    const hex = rgbToHexStr(r, g, b);
    if (seen.has(hex)) continue;
    seen.add(hex);
    out.push(hex);
    if (out.length >= max) break;
  }
  return out;
}

// ---------- Constants ----------

// Edge padding (fraction of canvas) — text box must stay within this inset.
export const OVERLAY_PAD_FRAC = 0.04;

// Reference canvas width used when authoring fontSize values. Real output
// canvases (1024, 1200, etc.) scale linearly from here.
export const REFERENCE_CANVAS_WIDTH = 1200;

// ---------- Anchor + clamp helpers ----------

// Convert anchor → [ax, ay] where 0 = element's left/top, 1 = element's right/bottom.
export function anchorOffsets(anchor: OverlayAnchor): { ax: number; ay: number } {
  const ax = anchor.endsWith('l') ? 0 : anchor.endsWith('r') ? 1 : 0.5;
  const ay = anchor.startsWith('t') ? 0 : anchor.startsWith('b') ? 1 : 0.5;
  return { ax, ay };
}

// Clamp anchor point so the rendered box (wFrac × hFrac of canvas) stays inside
// the padding inset.
export function clampAnchor(
  x: number,
  y: number,
  wFrac: number,
  hFrac: number,
  anchor: OverlayAnchor,
  padFrac = OVERLAY_PAD_FRAC,
): { x: number; y: number } {
  const { ax, ay } = anchorOffsets(anchor);
  let minX = padFrac + ax * wFrac;
  let maxX = 1 - padFrac - (1 - ax) * wFrac;
  let minY = padFrac + ay * hFrac;
  let maxY = 1 - padFrac - (1 - ay) * hFrac;
  // If the box can't fit, center the available range.
  if (minX > maxX) minX = maxX = (minX + maxX) / 2;
  if (minY > maxY) minY = maxY = (minY + maxY) / 2;
  return {
    x: Math.max(minX, Math.min(maxX, x)),
    y: Math.max(minY, Math.min(maxY, y)),
  };
}

// Map a snap id (e.g. 'top-left') to anchor point, anchor corner, and text alignment.
// Anchor sits at the inset edge so the text reads inward — left snaps anchor the left
// edge, right snaps anchor the right edge, etc.
export function snapForPosition(positionId: string): {
  x: number; y: number; anchor: OverlayAnchor; textAlign: OverlayTextAlign;
} {
  const left = positionId.includes('left');
  const right = positionId.includes('right');
  const top = positionId.startsWith('top');
  const bottom = positionId.startsWith('bottom');
  const pad = OVERLAY_PAD_FRAC;

  const x = left ? pad : right ? 1 - pad : 0.5;
  const y = top ? pad : bottom ? 1 - pad : 0.5;
  const horiz: 'l' | 'c' | 'r' = left ? 'l' : right ? 'r' : 'c';
  const vert: 't' | 'm' | 'b' = top ? 't' : bottom ? 'b' : 'm';
  const anchor = `${vert}${horiz}` as OverlayAnchor;
  const textAlign: OverlayTextAlign = left ? 'left' : right ? 'right' : 'center';
  return { x, y, anchor, textAlign };
}

// ---------- Text wrapping ----------

// Greedy word-wrap for canvas text. A word longer than maxWidth keeps its own line
// (it'll overflow visually, but the algorithm doesn't infinite-loop on it).
export function wrapTextToWidth(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const lines: string[] = [];
  const paragraphs = text.split('\n');
  for (const para of paragraphs) {
    const words = para.split(/\s+/).filter(Boolean);
    if (words.length === 0) { lines.push(''); continue; }
    let current = '';
    for (const w of words) {
      const candidate = current ? `${current} ${w}` : w;
      if (!current || ctx.measureText(candidate).width <= maxWidth) {
        current = candidate;
      } else {
        lines.push(current);
        current = w;
      }
    }
    if (current) lines.push(current);
  }
  return lines;
}

// ---------- DOM rendering ----------

// React inline-style for a text layer. Caller positions it via absolute
// left/top fed from layer.x / layer.y, and applies any preview-only font
// scaling (the preview canvas is smaller than 1200px). When backgroundColor
// is set, CSS padding makes the box hug the text — no measurement needed.
export function getTextLayerStyle(
  layer: TextLayer,
  options: { previewFontScale?: number } = {},
): CSSProperties {
  const { ax, ay } = anchorOffsets(layer.anchor);
  const scale = options.previewFontScale ?? 1;
  const effFontSize = getEffectiveFontSize(layer);
  // Compute max-width in PIXELS based on the actual rendered canvas size.
  // The caller's previewFontScale is `previewW / REFERENCE_CANVAS_WIDTH`, so
  // multiplying back recovers the parent's pixel width. We use pixel values
  // (not %) because Chrome doesn't reliably clamp percentage max-width
  // against intrinsic widths (max-content / fit-content) when box-sizing is
  // content-box — long text renders at natural width and overflows the cap.
  // Pixel max-width clamps consistently.
  const maxWidthPx = layer.maxWidthFrac * REFERENCE_CANVAS_WIDTH * scale;
  const style: CSSProperties = {
    left: `${layer.x * 100}%`,
    top: `${layer.y * 100}%`,
    transform: `translate(${-ax * 100}%, ${-ay * 100}%)`,
    // content-box so `maxWidth` caps the TEXT area only — padding extends the
    // box outward. Mirrors the canvas renderer, where maxWidth is the wrap
    // width for text and the background grows to `text + 2*padding`. Tailwind's
    // default is border-box, which would shrink the text area by padding and
    // cause shorter wraps in preview than in download.
    boxSizing: 'content-box',
    // `width: max-content` makes the element size to its natural content width
    // (then clamped by max-width). Without this, the browser's shrink-to-fit
    // caps width by the AVAILABLE SPACE to the parent's right edge for
    // absolutely-positioned elements — so a right-side layer (e.g. CTA at
    // x=0.77) gets squeezed and wraps unnecessarily. With max-content + a
    // pixel max-width clamp, both the right-edge case and the long-tagline
    // wrap case work reliably.
    width: 'max-content',
    maxWidth: `${maxWidthPx}px`,
    fontFamily: `${layer.fontFamily}, Inter, system-ui, sans-serif`,
    fontSize: `${effFontSize * scale}px`,
    fontWeight: layer.fontWeight,
    color: layer.color,
    textAlign: layer.textAlign,
    textTransform: layer.textTransform,
    // Word-only wrap (matches the canvas renderer). Without this, the DOM
    // would break mid-word on overflow and drift from the downloaded PNG.
    wordBreak: 'normal',
    overflowWrap: 'normal',
    lineHeight: 1.2,
    touchAction: 'none',
  };
  if (layer.backgroundColor) {
    style.backgroundColor = layer.backgroundColor;
    const pad = (layer.backgroundPadding ?? 0) * scale;
    style.padding = `${pad}px`;
    if (layer.backgroundRadius) {
      // CSS auto-caps border-radius at 50% of the box, so a large value
      // yields a full pill. Scaled to the preview size like everything else.
      style.borderRadius = `${layer.backgroundRadius * scale}px`;
    }
  }
  if (layer.offsetShadow) {
    // CSS box-shadow with 0 blur + 0 spread = a solid color block offset
    // from the border box. Auto-hugs the text + padding (border-box edge)
    // exactly like a sibling rect would, but with no extra DOM nodes.
    const offX = layer.offsetShadow.offsetX * scale;
    const offY = layer.offsetShadow.offsetY * scale;
    style.boxShadow = `${offX}px ${offY}px 0 0 ${layer.offsetShadow.color}`;
  }
  if (layer.textStrokeColor && layer.textStrokeWidth) {
    // -webkit-text-stroke is supported in Chrome, Safari, Edge, Firefox.
    // Setting both the prefixed and standardish names keeps us covered as
    // Firefox completes the unprefixed implementation.
    const strokeStyle = style as CSSProperties & {
      WebkitTextStrokeColor?: string;
      WebkitTextStrokeWidth?: string;
      textStrokeColor?: string;
      textStrokeWidth?: string;
    };
    strokeStyle.WebkitTextStrokeColor = layer.textStrokeColor;
    strokeStyle.WebkitTextStrokeWidth = `${layer.textStrokeWidth * scale}px`;
    strokeStyle.textStrokeColor = layer.textStrokeColor;
    strokeStyle.textStrokeWidth = `${layer.textStrokeWidth * scale}px`;
  }
  return style;
}

// Pre-transform the layer's text per its textTransform setting.
export function applyTextTransform(text: string, transform: OverlayTextTransform): string {
  if (transform === 'uppercase') return text.toUpperCase();
  if (transform === 'lowercase') return text.toLowerCase();
  return text;
}

// ---------- Canvas rendering ----------

// Accepts named weights ('normal' / 'medium' / 'semibold' / 'bold') or numeric
// strings ('400' / '500' / '600' / '700' etc.). Unknown values fall back to 400.
function fontWeightToNumber(weight: string): number {
  if (weight === 'bold') return 700;
  if (weight === 'semibold') return 600;
  if (weight === 'medium') return 500;
  if (weight === 'normal') return 400;
  const n = parseInt(weight, 10);
  if (!Number.isNaN(n) && n >= 100 && n <= 900) return n;
  return 400;
}

function renderTextLayerToCanvas(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
  layer: TextLayer,
): void {
  const text = applyTextTransform(layer.text, layer.textTransform);
  const weight = fontWeightToNumber(layer.fontWeight);
  const scale = canvasW / REFERENCE_CANVAS_WIDTH;
  const scaledFontSize = getEffectiveFontSize(layer) * scale;
  const lineHeight = scaledFontSize * 1.2;
  // Half-leading: CSS line-height distributes the extra (lineHeight - fontSize)
  // space half above and half below the EM box. Canvas textBaseline='top' draws
  // the EM box flush at the y; offsetting by halfLeading recenters it inside
  // the line-box so download matches the DOM preview exactly.
  const halfLeading = (lineHeight - scaledFontSize) / 2;
  const padPx = (layer.backgroundPadding ?? 0) * scale;

  ctx.save();
  ctx.font = `${weight} ${scaledFontSize}px ${layer.fontFamily}, Inter, system-ui, sans-serif`;

  // Wrap text and measure actual rendered widths — used both for the background
  // size (hugs content) and to keep anchor positioning consistent with DOM.
  const maxWidth = canvasW * layer.maxWidthFrac;
  const lines = wrapTextToWidth(ctx, text, maxWidth);
  let measuredW = 0;
  for (const line of lines) {
    measuredW = Math.max(measuredW, ctx.measureText(line).width);
  }
  const textBlockW = measuredW;
  const textBlockH = lines.length * lineHeight;
  const boxW = textBlockW + 2 * padPx;
  const boxH = textBlockH + 2 * padPx;

  const { ax, ay } = anchorOffsets(layer.anchor);
  const cx = canvasW * layer.x;
  const cy = canvasH * layer.y;
  const boxLeft = cx - ax * boxW;
  const boxTop = cy - ay * boxH;

  // Pill/rounded background — clamp the requested radius to half the box's
  // smaller side so a large value (full pill) can't over-round. roundRect is
  // supported in the browsers html-to-image runs in; fall back to square if
  // absent. Matches the DOM border-radius above so preview == downloaded PNG.
  const bgRadius = layer.backgroundRadius
    ? Math.min(layer.backgroundRadius * scale, boxW / 2, boxH / 2)
    : 0;
  const fillBox = (x: number, y: number, w: number, h: number) => {
    if (bgRadius > 0 && typeof ctx.roundRect === 'function') {
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, bgRadius);
      ctx.fill();
    } else {
      ctx.fillRect(x, y, w, h);
    }
  };
  if (layer.offsetShadow) {
    // Draw offset shadow rect FIRST so the bg rect lands on top of it.
    // Same dimensions as the bg, shifted by (offsetX, offsetY) at scale.
    const offX = layer.offsetShadow.offsetX * scale;
    const offY = layer.offsetShadow.offsetY * scale;
    ctx.fillStyle = layer.offsetShadow.color;
    fillBox(boxLeft + offX, boxTop + offY, boxW, boxH);
  }
  if (layer.backgroundColor) {
    ctx.fillStyle = layer.backgroundColor;
    fillBox(boxLeft, boxTop, boxW, boxH);
  }

  // Text drawing baseline starts inside the padding.
  const textLeft = boxLeft + padPx;
  const textTop = boxTop + padPx;
  ctx.fillStyle = layer.color;

  let drawX: number;
  if (layer.textAlign === 'left') {
    ctx.textAlign = 'left';
    drawX = textLeft;
  } else if (layer.textAlign === 'right') {
    ctx.textAlign = 'right';
    drawX = textLeft + textBlockW;
  } else {
    ctx.textAlign = 'center';
    drawX = textLeft + textBlockW / 2;
  }
  ctx.textBaseline = 'top';
  lines.forEach((line, i) => {
    ctx.fillText(line, drawX, textTop + halfLeading + i * lineHeight);
  });
  // Stroke pass — drawn AFTER fill so the outline reads cleanly on top.
  // For pure outline (no fill), set color: 'transparent' on the layer; the
  // fillText above is then a no-op visually and only the stroke shows.
  if (layer.textStrokeColor && layer.textStrokeWidth) {
    ctx.lineWidth = layer.textStrokeWidth * scale;
    ctx.strokeStyle = layer.textStrokeColor;
    ctx.lineJoin = 'round';
    ctx.miterLimit = 2;
    lines.forEach((line, i) => {
      ctx.strokeText(line, drawX, textTop + halfLeading + i * lineHeight);
    });
  }
  ctx.restore();
}

function renderShapeLayerToCanvas(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
  layer: ShapeLayer,
): void {
  const x = canvasW * layer.x;
  const y = canvasH * layer.y;
  const w = canvasW * layer.w;
  const h = canvasH * layer.h;

  ctx.save();
  ctx.globalAlpha = layer.opacity;
  ctx.fillStyle = layer.color;
  if (layer.rotation) {
    ctx.translate(x + w / 2, y + h / 2);
    ctx.rotate((layer.rotation * Math.PI) / 180);
    ctx.translate(-(x + w / 2), -(y + h / 2));
  }

  if (layer.shape === 'rect' || layer.shape === 'line') {
    ctx.fillRect(x, y, w, h);
  } else if (layer.shape === 'circle') {
    ctx.beginPath();
    ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

// Render an ordered list of layers onto the given context. Shapes are drawn
// first when interleaved; the caller controls order via the input array.
// Stacking (stackOnPrevious / stackGap) is resolved here so canvas output
// always matches the current text lengths — same code path as the DOM
// preview, just applied at draw time instead of mount time.
export function renderLayersToCanvas(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
  layers: Layer[],
): void {
  const resolved = resolveStackedLayers(layers, canvasW, canvasH);
  for (const layer of resolved) {
    if (layer.kind === 'text') {
      renderTextLayerToCanvas(ctx, canvasW, canvasH, layer);
    } else {
      renderShapeLayerToCanvas(ctx, canvasW, canvasH, layer);
    }
  }
}

// ---------- Flatten a composed frame to a PNG blob ----------
// Extracted from StepText (Jun 12) so the /appv3 Studio render panel can
// flatten frames OUTSIDE the StepText component (auto-save + the
// replace-image sync need the same pixels the editor previews). StepText's
// flattenFrameToBlob delegates here — ONE implementation, zero drift
// between the wizard download and the Studio's saved creative.
//
// Pipeline: await document.fonts.ready (canvas text metrics must match the
// DOM preview) → draw base image at natural size → shapes + text via
// renderLayersToCanvas (stacking resolved at draw time) → logos LAST with
// the same tint compositing the DOM preview uses (source-in offscreen).
export async function flattenComposedFrame(opts: {
  imageSrc: string;
  frameIndex: number;
  overlays: TextOverlay[];
  shapeOverlays: ShapeOverlay[];
  logoOverlays: LogoOverlay[];
}): Promise<Blob | null> {
  const { imageSrc, frameIndex, overlays, shapeOverlays, logoOverlays } = opts;
  if (!imageSrc) return null;

  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready;
  }

  const baseImg = new Image();
  baseImg.src = imageSrc;
  await new Promise<void>((resolve) => {
    baseImg.onload = () => resolve();
    baseImg.onerror = () => resolve();
  });

  const canvas = document.createElement('canvas');
  canvas.width = baseImg.naturalWidth || 1024;
  canvas.height = baseImg.naturalHeight || 1024;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(baseImg, 0, 0);

  // Shapes drawn first (under text); both filtered to the requested frame.
  const frameShapes = shapeOverlays.filter((s) => s.imageIndex === frameIndex);
  const frameTexts = overlays.filter((o) => o.imageIndex === frameIndex);
  const frameLayers: Layer[] = [...frameShapes, ...frameTexts];
  renderLayersToCanvas(ctx, canvas.width, canvas.height, frameLayers);

  // Logos drawn LAST (on top of text). They live outside the Layer union
  // because they need an async image preload — handled inline. Skipped
  // silently on preload error so a stale brand-logo URL doesn't kill the
  // whole export.
  const frameLogos = logoOverlays.filter((l) => l.imageIndex === frameIndex);
  for (const logo of frameLogos) {
    try {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.src = logo.src;
      await new Promise<void>((resolve, reject) => {
        logoImg.onload = () => resolve();
        logoImg.onerror = () => reject(new Error('logo load failed'));
      });
      const drawW = canvas.width * logo.widthFrac;
      const naturalAspect = logoImg.naturalHeight > 0
        ? logoImg.naturalWidth / logoImg.naturalHeight
        : 1;
      const drawH = naturalAspect > 0 ? drawW / naturalAspect : drawW;
      const drawX = canvas.width * logo.x;
      const drawY = canvas.height * logo.y;
      ctx.save();
      ctx.globalAlpha = logo.opacity;
      if (logo.tint === 'black' || logo.tint === 'white') {
        // Monochrome silhouette path — offscreen + 'source-in' keeps the
        // alpha mask while replacing every opaque pixel with the target
        // color (mirrors the DOM preview's CSS-filter treatment).
        const off = document.createElement('canvas');
        off.width = Math.max(1, Math.round(drawW));
        off.height = Math.max(1, Math.round(drawH));
        const offCtx = off.getContext('2d');
        if (offCtx) {
          offCtx.drawImage(logoImg, 0, 0, off.width, off.height);
          offCtx.globalCompositeOperation = 'source-in';
          offCtx.fillStyle = logo.tint === 'white' ? '#ffffff' : '#000000';
          offCtx.fillRect(0, 0, off.width, off.height);
          ctx.drawImage(off, drawX, drawY);
        } else {
          ctx.drawImage(logoImg, drawX, drawY, drawW, drawH);
        }
      } else {
        ctx.drawImage(logoImg, drawX, drawY, drawW, drawH);
      }
      ctx.restore();
    } catch (err) {
      console.warn('[flattenComposedFrame] logo render skipped:', err);
    }
  }

  return await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
}
